import React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Mail, Phone, MapPin, Send, Clock, ExternalLink } from 'lucide-react';
import FadeIn from '../../../components/animations/FadeIn';
import { contactService } from '../../../services/contactService';

const OFFICE_ADDRESS = '301, De-Elmas, Sonawala Road, Goregaon(East), Mumbai-400063';
const GOOGLE_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(OFFICE_ADDRESS)}`;
const GOOGLE_MAPS_EMBED_URL = `https://maps.google.com/maps?q=${encodeURIComponent(OFFICE_ADDRESS)}&output=embed`;

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    details: ['+91 9833916981'],
  },
  {
    icon: Mail,
    title: 'Email',
    details: ['sales@siwi.in'],
  },
  {
    icon: MapPin,
    title: 'Office (R&D)',
    details: [OFFICE_ADDRESS],
  },
];

const Contact = () => {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const onSubmit = async (data) => {
    try {
      await contactService.submit(data);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      reset();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    }
  };

  return (
    <div id="contact" className="min-h-screen bg-gray-50 pt-16">

      {/* Hero - increased height */}
      <div className="relative min-h-[500px] flex items-center overflow-hidden">
        <img
          src="/assets/images/contact.png"
          alt="Contact Srishti Wireless Solution"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gray-900/70" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white w-full">
          <FadeIn>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Have a project in mind? Let's discuss how we can help transform your business with IoT solutions.
            </p>
          </FadeIn>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-16">

          {/* Contact Info */}
          <div>
            <FadeIn direction="left">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Whether you have a question about our products, need a custom solution,
                or just want to say hello, our team is ready to answer all your questions.
              </p>
            </FadeIn>

            <div className="grid sm:grid-cols-2 gap-6">
              {contactInfo.map((item, index) => (
                <FadeIn key={item.title} direction="left" delay={index * 0.1}>
                  <div className="bg-white p-6 rounded border border-gray-200 hover:border-sky-300 hover:shadow-md transition-all duration-300">
                    <div className="w-12 h-12 rounded bg-sky-50 text-sky-600 flex items-center justify-center mb-4">
                      <item.icon size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                    {item.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                    ))}
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <FadeIn direction="right">
            <div className="bg-white p-6 md:p-8 rounded border border-gray-200 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-5">Send us a Message</h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      {...register('name', { required: 'Name is required' })}
                      className="w-full px-4 py-3 rounded border border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all bg-gray-50 focus:bg-white"
                      placeholder="John Doe"
                    />
                    {errors.name && <span className="text-red-500 text-sm mt-1">{errors.name.message}</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <input
                      {...register('email', {
                        required: 'Email is required',
                        pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' },
                      })}
                      className="w-full px-4 py-3 rounded border border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all bg-gray-50 focus:bg-white"
                      placeholder="john@example.com"
                    />
                    {errors.email && <span className="text-red-500 text-sm mt-1">{errors.email.message}</span>}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      {...register('phone')}
                      className="w-full px-4 py-3 rounded border border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all bg-gray-50 focus:bg-white"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <input
                      {...register('subject', { required: 'Subject is required' })}
                      className="w-full px-4 py-3 rounded border border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all bg-gray-50 focus:bg-white"
                      placeholder="Project Inquiry"
                    />
                    {errors.subject && <span className="text-red-500 text-sm mt-1">{errors.subject.message}</span>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    {...register('message', { required: 'Message is required' })}
                    rows={4}
                    className="w-full px-4 py-3 rounded border border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all resize-none bg-gray-50 focus:bg-white"
                    placeholder="Tell us about your project requirements..."
                  />
                  {errors.message && <span className="text-red-500 text-sm mt-1">{errors.message.message}</span>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gray-600 text-white font-semibold rounded hover:bg-gray-700 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  <Send size={20} />
                </button>
              </form>
            </div>
          </FadeIn>

        </div>

        {/* Map - full width, spanning left edge to form's right border */}
        <FadeIn direction="up" delay={0.4}>
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group block mt-12 w-full rounded border border-gray-200 overflow-hidden hover:border-sky-300 hover:shadow-md transition-all duration-300 relative"
          >
            <iframe
              title="Office Location"
              src={GOOGLE_MAPS_EMBED_URL}
              className="w-full h-80 pointer-events-none"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
            <div className="absolute inset-0 bg-gray-900/0 group-hover:bg-gray-900/10 transition-colors duration-300 flex items-end justify-end p-4">
              <span className="inline-flex items-center gap-1.5 bg-white text-gray-900 text-xs font-semibold px-3 py-1.5 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Open in Google Maps
                <ExternalLink size={12} />
              </span>
            </div>
          </a>
        </FadeIn>
      </div>
    </div>
  );
};

export default Contact;